import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Category labels
const CATEGORY_LABELS = {
  'bracelets': 'Bracelets',
  'anklets': 'Anklets',
  'tie-dye': 'Tie Dye Apparel',
  'baskets': 'Woven Baskets'
};

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('mrsbum') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

function MrsBumCategory() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { itemCount, openCart } = useCart();

  const categoryLabel = CATEGORY_LABELS[category] || category;

  useEffect(() => {
    fetchProducts();
  }, [category]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/mrsbum/category/${category}`);
      setProducts(response.data.filter(p => p.active));
    } catch (error) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      {/* Breadcrumb */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px 20px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <Link 
            to="/shop/mrsbum" 
            style={{ color: '#7c3aed', textDecoration: 'none' }}
          >
            mrs.bum
          </Link>
          <span style={{ color: '#ccc' }}>&gt;</span>
          <span style={{ color: '#1a1a1a' }}>{categoryLabel}</span>
        </div>
      </div>

      {/* Main Content - Product Grid */}
      <main style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '30px 20px 60px' 
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="loading-spinner" />
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
              No {categoryLabel.toLowerCase()} available yet. Check back soon!
            </p>
            <Link to="/shop/mrsbum">
              <Button variant="outline" style={{ borderColor: '#7c3aed', color: '#7c3aed' }}>
                <ArrowLeft size={18} style={{ marginRight: '8px' }} />
                Back to Mrs. Bum
              </Button>
            </Link>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '25px'
          }}>
            {products.map(product => (
              <ProductCard key={product.id} product={product} category={category} />
            ))}
          </div>
        )}

        <style>{`
          @media (max-width: 1024px) {
            main > div:last-of-type {
              grid-template-columns: repeat(3, 1fr) !important;
            }
          }
          @media (max-width: 768px) {
            main > div:last-of-type {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
          @media (max-width: 480px) {
            main > div:last-of-type {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />
    </div>
  );
}

// Product Card Component - Links to product detail page
function ProductCard({ product, category }) {
  return (
    <Link 
      to={`/shop/mrsbum/${category}/${product.id}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
      data-testid={`product-${product.id}`}
    >
      <div 
        style={{
          background: '#fff',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          cursor: 'pointer'
        }}
        className="mrsbum-product-card"
      >
        {/* Product Image */}
        <div style={{
          aspectRatio: '1',
          overflow: 'hidden',
          background: '#f8f4ff'
        }}>
          <img 
            src={product.image_url} 
            alt={product.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.3s',
              opacity: product.one_of_a_kind && product.stock === 0 ? 0.6 : 1
            }}
          />
        </div>
        
        {/* Product Info */}
        <div style={{ padding: '15px', textAlign: 'center' }}>
          <h3 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '16px',
            fontWeight: '400',
            margin: '0 0 5px',
            color: '#1a1a1a'
          }}>
            {product.title}
          </h3>
          
          <span style={{
            fontSize: '15px',
            color: '#7c3aed'
          }}>
            ${product.price}
          </span>
          {product.one_of_a_kind && (
            <div style={{
              fontSize: '12px', fontWeight: '600', marginTop: '4px',
              color: product.stock === 0 ? '#6b7280' : '#7c3aed'
            }}>
              {product.stock === 0 ? 'Sold' : 'One of a kind'}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .mrsbum-product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(124, 58, 237, 0.12);
        }
        .mrsbum-product-card:hover img {
          transform: scale(1.05);
        }
      `}</style>
    </Link>
  );
}

export default MrsBumCategory;
